/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, MapPin, TrendingUp, Sparkles } from 'lucide-react';
import ConfidenceChip from '@/components/terra/ConfidenceChip';

const MATURITY_TONE = {
  SEEDING:      'bg-stone-100   text-stone-700   border-stone-300',
  WARMING:      'bg-amber-50    text-amber-800   border-amber-300',
  ACCELERATING: 'bg-sky-50      text-sky-800     border-sky-300',
  MATURING:     'bg-violet-50   text-violet-800  border-violet-300',
  FLOURISHING:  'bg-emerald-50  text-emerald-800 border-emerald-300',
};

export default function TerritoryMaturityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/terra-growth/intelligence/territory-maturity`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="territory-maturity-page" className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Territory Maturity</h1>
          <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
            Per-centre fingerprint — how each territory is evolving from awareness through advocate density.
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Computing…
          </div>
        ) : !data || data.centres.length === 0 ? (
          <div data-testid="territory-maturity-empty"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            No centre data yet. Tag leads with a centre to populate.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.centres.map(c => (
              <CentreCard key={c.centre_id} c={c} />
            ))}
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function CentreCard({ c }) {
  const mat = c.maturity || {};
  return (
    <article
      data-testid={`territory-card-${c.centre_id}`}
      className="rounded-2xl border border-[#EBE8E3] bg-white p-5 space-y-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-3.5 h-3.5 text-[#8A8681]" />
          <p className="text-sm text-[#2C2A29] truncate font-['Work_Sans']">{c.centre_id}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${MATURITY_TONE[c.maturity_label] || ''}`}>
          {mat.label || c.maturity_label}
        </span>
      </div>

      <p className="text-[11px] text-[#8A8681] leading-snug">{mat.description}</p>

      <div className="grid grid-cols-2 gap-2 mt-2">
        <Mini label="Lead total"     value={c.lead_total} />
        <Mini label="Movements · 30d" value={c.movements_30d} icon={<TrendingUp className="w-3 h-3" />} />
        <Mini label="Conversion lag" value={c.conversion_lag_days_avg ? `${c.conversion_lag_days_avg}d` : '—'} />
        <Mini label="Advocate density" value={`${c.advocate_density}%`} icon={<Sparkles className="w-3 h-3" />} />
      </div>

      <div className="space-y-1.5 pt-1">
        <Bar label="Awareness band"   pct={c.awareness_band_pct}   tone="bg-stone-400" />
        <Bar label="Evaluation band"  pct={c.evaluation_band_pct}  tone="bg-amber-500" />
        <Bar label="Decision band"    pct={c.decision_band_pct}    tone="bg-sky-500" />
        <Bar label="Subscriber band"  pct={c.subscriber_band_pct}  tone="bg-emerald-600" />
      </div>

      <div className="pt-1">
        <ConfidenceChip code={c.confidence} sample={c.movements_30d} compact />
      </div>
    </article>
  );
}

function Mini({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-[#EBE8E3] bg-[#FBFaf9] px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[#8A8681] flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-base font-['Outfit'] tabular-nums text-[#2C2A29] mt-0.5">{value ?? '—'}</div>
    </div>
  );
}

function Bar({ label, pct, tone }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8681] w-32 truncate">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-[#F4F2EE] overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct || 0)}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-[#5C5955] w-10 text-right">{pct}%</span>
    </div>
  );
}
